import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "../lib/cn.js";

/**
 * PlayerMagnet — circular pill used on tactical boards (DESIGN.md "Tactical Elements").
 * Black/white default; switch to `team="opponent"` for the Electric Blue accent.
 * Renders a small caps label under the disc.
 */
export interface PlayerMagnetProps extends HTMLAttributes<HTMLDivElement> {
  number?: number | string;
  name?: string;
  team?: "own" | "opponent";
  selected?: boolean;
  size?: "sm" | "md" | "lg";
}

const sizeMap = {
  sm: "size-8 text-body-sm",
  md: "size-10 text-body-lg",
  lg: "size-14 text-title-md",
} as const;

export const PlayerMagnet = forwardRef<HTMLDivElement, PlayerMagnetProps>(function PlayerMagnet(
  { number, name, team = "own", selected = false, size = "md", className, ...props },
  ref,
) {
  return (
    <div ref={ref} className={cn("inline-flex flex-col items-center gap-1", className)} {...props}>
      <div
        data-team={team}
        data-selected={selected}
        className={cn(
          "flex items-center justify-center rounded-full font-semibold tabular-nums select-none",
          "transition-shadow duration-fast ease-standard",
          team === "own" ? "bg-foreground text-background" : "bg-tactical-blue text-white",
          selected && "shadow-glow ring-2 ring-pitch-lime",
          sizeMap[size],
        )}
      >
        {number ?? ""}
      </div>
      {name && (
        <span className="text-label-caps uppercase tracking-wider text-muted-foreground max-w-[8ch] truncate">
          {name}
        </span>
      )}
    </div>
  );
});
